import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import messageService from '../../services/messageService';

const ACCENT = '#ffcc00';
const DARK = 'rgb(28,37,54)';

const useStyles = makeStyles(() => ({
  root: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  body: { flex: 1, overflowY: 'auto', padding: '20px 24px', '@media (max-width:480px)': { padding: '16px 12px' } },
  form: { maxWidth: 700, display: 'flex', flexDirection: 'column', gap: 16 },

  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  labelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  label: { fontSize: 12, fontWeight: 600, color: '#374151', letterSpacing: '0.03em', textTransform: 'uppercase' },
  required: { color: '#ef4444', marginLeft: 3 },
  linkBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: 12,
    color: '#6b7280', textDecoration: 'underline', padding: 0,
    '&:hover': { color: '#374151' }, '&:disabled': { opacity: 0.4, cursor: 'default' },
  },
  input: {
    padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
    color: '#111827', outline: 'none', background: '#fff', transition: 'border-color 0.15s',
    '&:focus': { borderColor: ACCENT },
  },
  inputError: { borderColor: '#ef4444 !important' },
  errorMsg: { fontSize: 11.5, color: '#ef4444', marginTop: 2 },

  /* ── RECIPIENT DROPDOWN ── */
  rcpContainer: { position: 'relative' },

  /* chips row above the trigger */
  chipsRow: { display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    background: DARK, color: '#fff', borderRadius: 5,
    padding: '4px 9px', fontSize: 12.5, fontWeight: 500,
  },
  chipRemove: {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.65)',
    cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1,
    '&:hover': { color: '#fff' },
  },

  /* trigger button */
  trigger: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6,
    background: '#fff', cursor: 'pointer', fontSize: 14, color: '#111827',
    transition: 'border-color 0.15s', textAlign: 'left', gap: 8,
  },
  triggerOpen: { borderColor: ACCENT, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
  triggerError: { borderColor: '#ef4444 !important' },
  triggerPlaceholder: { color: '#9ca3af' },
  triggerCaret: { color: '#6b7280', fontSize: 12, flexShrink: 0, transition: 'transform 0.15s' },
  triggerCaretOpen: { transform: 'rotate(180deg)' },

  /* dropdown panel */
  panel: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
    background: '#fff', border: '1px solid #d1d5db', borderTop: 'none',
    borderBottomLeftRadius: 6, borderBottomRightRadius: 6,
    boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
    display: 'flex', flexDirection: 'column',
  },

  /* search inside panel */
  panelSearch: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 12px', borderBottom: '1px solid #f0f1f3',
    background: '#f9fafb',
  },
  panelSearchInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: 13.5,
    background: 'transparent', color: '#111827',
  },
  panelList: { maxHeight: 230, overflowY: 'auto' },

  panelItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 12px', cursor: 'pointer', userSelect: 'none',
    '&:hover': { background: '#f9fafb' },
  },
  panelItemSelected: { background: '#fefce8 !important' },
  panelAvatar: {
    width: 30, height: 30, borderRadius: '50%', background: DARK,
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700, flexShrink: 0,
  },
  panelAvatarSelected: { background: ACCENT + ' !important', color: DARK + ' !important' },
  panelName: { fontWeight: 500, fontSize: 13.5, color: '#111827' },
  panelType: { fontSize: 11.5, color: '#9ca3af' },
  panelCheck: { marginLeft: 'auto', color: ACCENT, fontSize: 14, flexShrink: 0 },
  panelEmpty: { padding: '16px 12px', textAlign: 'center', color: '#9ca3af', fontSize: 13 },

  panelFooter: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '7px 12px', borderTop: '1px solid #f0f1f3', background: '#f9fafb',
    fontSize: 12, color: '#6b7280',
  },

  /* ── TYPE + PRIORITY ── */
  toggleGroup: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  toggleBtn: {
    padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db',
    background: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500, color: '#374151',
    transition: 'all 0.12s', '&:hover': { background: '#f9fafb' },
  },
  toggleBtnActive: {
    background: `${DARK} !important`, color: '#fff !important', borderColor: `${DARK} !important`,
  },

  textarea: {
    padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
    color: '#111827', resize: 'vertical', minHeight: 200, outline: 'none',
    fontFamily: 'inherit', transition: 'border-color 0.15s',
    '&:focus': { borderColor: ACCENT },
  },
  charCount: { textAlign: 'right', fontSize: 11.5, color: '#9ca3af', marginTop: 2 },

  /* ── FOOTER ── */
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 24px',
    borderTop: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0,
    '@media (max-width:480px)': { padding: '10px 12px' },
  },
  btnCancel: {
    padding: '8px 20px', borderRadius: 6, border: '1px solid #d1d5db',
    background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#374151',
    '&:hover': { background: '#f3f4f6' },
  },
  btnSend: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 22px', borderRadius: 6, border: 'none',
    background: DARK, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#fff',
    '&:hover': { opacity: 0.88 }, '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },

  twoCol: { display: 'flex', gap: 16, '@media (max-width:600px)': { flexDirection: 'column' } },
}));

const MESSAGE_TYPES = [
  { value: 'INFO',     label: 'Info',     icon: 'fa-circle-info' },
  { value: 'FEEDBACK', label: 'Feedback', icon: 'fa-comment' },
  { value: 'ERROR',    label: 'Error',    icon: 'fa-triangle-exclamation' },
];
const PRIORITIES = [
  { value: 'normal', label: 'Normal' },
  { value: 'urgent', label: '⚠ Urgent' },
];

/* ─────────────────────────────────────────────
   Contact Dropdown component
───────────────────────────────────────────── */
function ContactDropdown({ contacts, recipients, setRecipients, error, loading }) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search when opening
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
    else setSearch('');
  }, [open]);

  const filtered = contacts.filter(c =>
    c.contactName?.toLowerCase().includes(search.toLowerCase())
  );
  const isSelected = (c) => recipients.some(r => r.contactId === c.contactId);

  const toggle = (c) => {
    if (isSelected(c)) {
      setRecipients(prev => prev.filter(r => r.contactId !== c.contactId));
    } else {
      setRecipients(prev => [...prev, c]);
    }
  };

  const removeChip = (e, id) => {
    e.stopPropagation();
    setRecipients(prev => prev.filter(r => r.contactId !== id));
  };

  const triggerLabel = recipients.length === 0
    ? null
    : recipients.length <= 2
    ? recipients.map(r => r.contactName).join(', ')
    : `${recipients.length} contacts selected`;

  return (
    <div ref={containerRef} className={classes.rcpContainer}>
      {/* Selected chips */}
      {recipients.length > 0 && (
        <div className={classes.chipsRow}>
          {recipients.map(r => (
            <span key={r.contactId} className={classes.chip}>
              {r.contactName}
              <button className={classes.chipRemove} onMouseDown={e => removeChip(e, r.contactId)}>✕</button>
            </span>
          ))}
        </div>
      )}

      {/* Trigger */}
      <button
        type="button"
        className={`${classes.trigger} ${open ? classes.triggerOpen : ''} ${error ? classes.triggerError : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        {loading
          ? <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#9ca3af' }}>
              <CircularProgress size={14} /> Loading contacts…
            </span>
          : <span className={!triggerLabel ? classes.triggerPlaceholder : ''}>
              {triggerLabel || 'Select contacts…'}
            </span>
        }
        <i className={`fa-solid fa-chevron-down ${classes.triggerCaret} ${open ? classes.triggerCaretOpen : ''}`} />
      </button>

      {/* Panel */}
      {open && (
        <div className={classes.panel}>
          {/* Search */}
          <div className={classes.panelSearch}>
            <i className="fa-solid fa-magnifying-glass" style={{ color: '#9ca3af', fontSize: 13 }} />
            <input
              ref={searchRef}
              className={classes.panelSearchInput}
              placeholder="Search contacts…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button onClick={() => setSearch('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}>
                <i className="fa-solid fa-xmark" style={{ fontSize: 13 }} />
              </button>
            )}
          </div>

          {/* List */}
          <div className={classes.panelList}>
            {filtered.length === 0
              ? <div className={classes.panelEmpty}>
                  {search ? 'No contacts match your search' : 'No contacts available'}
                </div>
              : filtered.map(c => {
                  const selected = isSelected(c);
                  return (
                    <div key={`${c.contactType}_${c.contactId}`}
                      className={`${classes.panelItem} ${selected ? classes.panelItemSelected : ''}`}
                      onMouseDown={() => toggle(c)}>
                      <div className={`${classes.panelAvatar} ${selected ? classes.panelAvatarSelected : ''}`}>
                        {(c.contactName?.charAt(0) || 'U').toUpperCase()}
                      </div>
                      <div>
                        <div className={classes.panelName}>{c.contactName}</div>
                        {c.contactType && <div className={classes.panelType}>{c.contactType}</div>}
                      </div>
                      {selected && <i className={`fa-solid fa-check ${classes.panelCheck}`} />}
                    </div>
                  );
                })
            }
          </div>

          {/* Footer */}
          <div className={classes.panelFooter}>
            <span>{recipients.length} / {contacts.length} selected</span>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className={classes.linkBtn}
                disabled={recipients.length === contacts.length}
                onMouseDown={() => setRecipients([...contacts])}>
                Select All
              </button>
              <button className={classes.linkBtn}
                disabled={recipients.length === 0}
                onMouseDown={() => setRecipients([])}>
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Compose component
───────────────────────────────────────────── */
export default function MessagesCompose({ userId }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const replyToId = params.get('replyTo') ? Number(params.get('replyTo')) : null;

  const [contacts, setContacts]     = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [messageType, setMessageType] = useState('INFO');
  const [priority, setPriority]     = useState('normal');
  const [subject, setSubject]       = useState('');
  const [content, setContent]       = useState('');
  const [sending, setSending]       = useState(false);
  const [submitted, setSubmitted]   = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoadingContacts(true);
    messageService.getContacts(userId)
      .then(r => {
        const d = r.data;
        let list = Array.isArray(d) ? d : d?.items || d?.data || [];
        list = list.filter(c => !c.isBlocked);
        setContacts(list);
        if (replyToId) {
          const found = list.find(c => c.contactId === replyToId);
          if (found) setRecipients([found]);
        }
        setLoadingContacts(false);
      })
      .catch(() => {
        toast.warning('Could not load contacts');
        setLoadingContacts(false);
      });
  }, [userId, replyToId]);

  const send = async () => {
    setSubmitted(true);
    if (!recipients.length || !subject.trim() || !content.trim()) return;
    setSending(true);
    try {
      await Promise.all(recipients.map(r =>
        messageService.sendMessage({
          senderId: 6948, recipientId: r.contactId,
          subject: subject.trim(), content: content.trim(),
          messageType, priority,
        })
      ));
      toast.success(`Sent to ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}`);
      history.push('/messages/inbox');
    } catch {
      toast.error('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.body}>
        <div className={classes.form}>

          {/* Recipients */}
          <div className={classes.field}>
            <div className={classes.labelRow}>
              <label className={classes.label}>To <span className={classes.required}>*</span></label>
            </div>
            <ContactDropdown
              contacts={contacts}
              recipients={recipients}
              setRecipients={setRecipients}
              error={submitted && !recipients.length}
              loading={loadingContacts}
            />
            {submitted && !recipients.length && (
              <span className={classes.errorMsg}>At least one recipient is required</span>
            )}
          </div>

          {/* Subject */}
          <div className={classes.field}>
            <label className={classes.label}>Subject <span className={classes.required}>*</span></label>
            <input
              className={`${classes.input} ${submitted && !subject.trim() ? classes.inputError : ''}`}
              placeholder="Enter subject…"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              maxLength={200}
            />
            {submitted && !subject.trim() && <span className={classes.errorMsg}>Subject is required</span>}
          </div>

          {/* Type + Priority */}
          <div className={classes.twoCol}>
            <div className={classes.field} style={{ flex: 1 }}>
              <label className={classes.label}>Message Type</label>
              <div className={classes.toggleGroup}>
                {MESSAGE_TYPES.map(t => (
                  <button key={t.value}
                    className={`${classes.toggleBtn} ${messageType === t.value ? classes.toggleBtnActive : ''}`}
                    onClick={() => setMessageType(t.value)}>
                    <i className={`fa-solid ${t.icon}`} style={{ marginRight: 6 }} />{t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className={classes.field}>
              <label className={classes.label}>Priority</label>
              <div className={classes.toggleGroup}>
                {PRIORITIES.map(p => (
                  <button key={p.value}
                    className={`${classes.toggleBtn} ${priority === p.value ? classes.toggleBtnActive : ''}`}
                    onClick={() => setPriority(p.value)}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={classes.field}>
            <label className={classes.label}>Message <span className={classes.required}>*</span></label>
            <textarea
              className={`${classes.textarea} ${submitted && !content.trim() ? classes.inputError : ''}`}
              placeholder="Write your message here…"
              value={content}
              onChange={e => setContent(e.target.value)}
              maxLength={5000}
            />
            <div className={classes.charCount}>{content.length} / 5000</div>
            {submitted && !content.trim() && <span className={classes.errorMsg}>Message is required</span>}
          </div>

        </div>
      </div>

      <div className={classes.footer}>
        <button className={classes.btnCancel} onClick={() => history.push('/messages/inbox')} disabled={sending}>
          Cancel
        </button>
        <button className={classes.btnSend} onClick={send} disabled={sending}>
          {sending
            ? <><CircularProgress size={14} style={{ color: '#fff' }} /> Sending…</>
            : <><i className="fa-solid fa-paper-plane" />{recipients.length > 1 ? `Send to ${recipients.length}` : 'Send'}</>
          }
        </button>
      </div>
    </div>
  );
}
